import { useCallback, useEffect, useRef, useState } from "react";
import {
  BRAND,
  CAKE_MARKER_SVG,
  HOME_MARKER_SVG,
  ROUTE_REFRESH_MIN_METERS_CUSTOMER,
  ROUTE_REFRESH_MIN_METERS_DRIVER,
  TABLETOP_MAP_STYLES,
  animateMarkerTo,
  estimateMinutesFromMeters,
  fetchDrivingRoute,
  fitMapToDriverAndDest,
  applyDriverMapCamera,
  fitMapToRoute,
  mapFitPadding,
  pathDistanceMeters,
  shouldRefreshRoute,
  smoothPanTo,
  trackingEtaReady,
  trimRouteFromPosition,
  waitForGoogleMaps,
} from "@/lib/liveTrackingMap";

/**
 * @param {object} opts
 * @param {React.RefObject} opts.mapEl
 * @param {object} opts.tracking
 * @param {boolean} opts.fullscreen
 * @param {{ lat: number, lng: number } | null} [opts.livePosition] — driver phone GPS (handoff window)
 * @param {boolean} [opts.locationSharing=false] — DA phone successfully posting GPS to server
 * @param {boolean} [opts.lockDestination=true] — never move home pin after first fix
 * @param {"driver"|"customer"} [opts.variant="customer"]
 */
export function useLiveDeliveryMap({
  mapEl,
  tracking,
  fullscreen,
  livePosition = null,
  locationSharing = false,
  lockDestination = true,
  variant = "customer",
}) {
  const mapObj = useRef(null);
  const routeLineRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const homeMarkerRef = useRef(null);
  const pathRef = useRef([]);
  const lockedDestRef = useRef(null);
  const lastRouteOriginRef = useRef(null);
  const lastRouteAtRef = useRef(0);
  const routeBusyRef = useRef(false);
  const didInitialFitRef = useRef(false);
  const driverPosRef = useRef(null);
  const routePollRef = useRef(null);
  const lastDriverZoomRefitRef = useRef(0);
  const [routeEtaMinutes, setRouteEtaMinutes] = useState(null);

  const isDriver = variant === "driver";
  const routeMinMeters = isDriver
    ? ROUTE_REFRESH_MIN_METERS_DRIVER
    : ROUTE_REFRESH_MIN_METERS_CUSTOMER;

  const etaReady = trackingEtaReady(tracking, {
    livePosition,
    locationSharing,
    variant,
  });
  const etaReadyRef = useRef(etaReady);
  etaReadyRef.current = etaReady;

  useEffect(() => {
    if (!etaReady) setRouteEtaMinutes(null);
  }, [etaReady]);

  const showMap = Boolean(tracking?.show_map);
  const destLatIn = tracking?.destination_lat;
  const destLngIn = tracking?.destination_lng;
  const bakeryLat = tracking?.bakery_lat;
  const bakeryLng = tracking?.bakery_lng;

  useEffect(() => {
    if (destLatIn == null || destLngIn == null) return;
    if (!lockDestination) {
      lockedDestRef.current = { lat: destLatIn, lng: destLngIn };
      return;
    }
    if (!lockedDestRef.current) {
      lockedDestRef.current = { lat: destLatIn, lng: destLngIn };
    }
  }, [destLatIn, destLngIn, lockDestination]);

  const destLat = lockedDestRef.current?.lat ?? destLatIn;
  const destLng = lockedDestRef.current?.lng ?? destLngIn;

  const serverDriverLat = tracking?.driver_lat;
  const serverDriverLng = tracking?.driver_lng;
  const driverLat = livePosition?.lat ?? serverDriverLat;
  const driverLng = livePosition?.lng ?? serverDriverLng;

  driverPosRef.current =
    driverLat != null && driverLng != null ? { lat: driverLat, lng: driverLng } : null;

  const hasDriverPosition = Boolean(
    livePosition?.lat != null ||
      tracking?.has_driver_position ||
      (serverDriverLat != null && serverDriverLng != null)
  );
  const hasFreshDriver = Boolean(livePosition || tracking?.has_driver_location);
  const driverName = tracking?.driver_name || "Your delivery partner";

  const destPoint =
    destLat != null && destLng != null ? { lat: destLat, lng: destLng } : null;

  const setPolylinePath = useCallback((path) => {
    if (!path?.length || !mapObj.current) return;
    pathRef.current = path;
    if (routeLineRef.current) {
      routeLineRef.current.setPath(path);
    } else {
      routeLineRef.current = new window.google.maps.Polyline({
        map: mapObj.current,
        path,
        geodesic: false,
        strokeColor: BRAND,
        strokeOpacity: 0.92,
        strokeWeight: 6,
      });
    }
  }, []);

  const fitCamera = useCallback(
    (map, path, driver, dest) => {
      if (!map) return;
      window.google?.maps?.event?.trigger(map, "resize");
      const padding = mapFitPadding(fullscreen, variant);
      const displayPath =
        isDriver && driver && path?.length > 1
          ? trimRouteFromPosition(path, driver)
          : path;

      if (isDriver && dest) {
        applyDriverMapCamera(map, displayPath, driver, dest, { fullscreen });
      } else if (driver && dest) {
        fitMapToDriverAndDest(map, driver, dest, padding, {
          minZoom: 13,
          maxZoom: 16,
        });
      } else if (displayPath?.length) {
        fitMapToRoute(map, displayPath, padding);
      }
      didInitialFitRef.current = true;
    },
    [fullscreen, variant, isDriver]
  );

  const applyRoutePath = useCallback(
    (map, path, { refit = false, driver = null, dest = null } = {}) => {
      if (!map || !path?.length) return;
      const displayPath =
        driver && path.length > 1 ? trimRouteFromPosition(path, driver) : path;
      setPolylinePath(displayPath);
      if (refit || !didInitialFitRef.current) {
        fitCamera(map, displayPath, driver, dest);
      }
    },
    [fitCamera, setPolylinePath]
  );

  const refreshDrivingRoute = useCallback(
    async (map, origin, destination, force = false) => {
      if (!map || !origin || !destination) return;
      const now = Date.now();
      if (
        !force &&
        !shouldRefreshRoute(
          lastRouteOriginRef.current,
          origin,
          lastRouteAtRef.current,
          now,
          routeMinMeters
        )
      ) {
        return;
      }
      if (routeBusyRef.current) return;
      routeBusyRef.current = true;
      try {
        const { path, durationMinutes } = await fetchDrivingRoute(origin, destination);
        lastRouteOriginRef.current = { lat: origin.lat, lng: origin.lng };
        lastRouteAtRef.current = now;
        if (durationMinutes != null && etaReadyRef.current) {
          setRouteEtaMinutes(durationMinutes);
        }
        applyRoutePath(map, path, {
          refit: force || !didInitialFitRef.current,
          driver: driverPosRef.current,
          dest: destPoint,
        });
      } finally {
        routeBusyRef.current = false;
      }
    },
    [applyRoutePath, destPoint, routeMinMeters]
  );

  // Map bootstrap + home marker + route polling (not on every GPS tick).
  useEffect(() => {
    if (!showMap || destLat == null || destLng == null) return undefined;
    let cancelled = false;

    const boot = async () => {
      const ready = await waitForGoogleMaps();
      if (!ready || cancelled || !mapEl.current) return;

      const dest = { lat: destLat, lng: destLng };
      const pos = driverPosRef.current;
      const hasCoords = hasDriverPosition && pos;
      const center = {
        lat: hasCoords ? (pos.lat + destLat) / 2 : (bakeryLat + destLat) / 2,
        lng: hasCoords ? (pos.lng + destLng) / 2 : (bakeryLng + destLng) / 2,
      };

      if (!mapObj.current) {
        mapObj.current = new window.google.maps.Map(mapEl.current, {
          center,
          zoom: isDriver ? 17 : 13,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
          styles: TABLETOP_MAP_STYLES,
        });
        didInitialFitRef.current = false;
      }

      const map = mapObj.current;

      if (!homeMarkerRef.current) {
        homeMarkerRef.current = new window.google.maps.Marker({
          map,
          position: dest,
          title: "Drop-off",
          icon: {
            url: `data:image/svg+xml,${HOME_MARKER_SVG}`,
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 22),
          },
          zIndex: 2,
        });
      }

      const routeOrigin = hasCoords
        ? pos
        : bakeryLat != null && bakeryLng != null
          ? { lat: bakeryLat, lng: bakeryLng }
          : null;

      if (routeOrigin) {
        await refreshDrivingRoute(map, routeOrigin, dest, !lastRouteOriginRef.current);
      }
    };

    boot();

    const pollMs = isDriver ? 12000 : 8000;
    routePollRef.current = window.setInterval(() => {
      const map = mapObj.current;
      const dest = destLat != null && destLng != null ? { lat: destLat, lng: destLng } : null;
      const pos = driverPosRef.current;
      const origin =
        pos ||
        (bakeryLat != null && bakeryLng != null ? { lat: bakeryLat, lng: bakeryLng } : null);
      if (map && dest && origin) {
        refreshDrivingRoute(map, origin, dest, false);
      }
    }, pollMs);

    return () => {
      cancelled = true;
      if (routePollRef.current) {
        window.clearInterval(routePollRef.current);
        routePollRef.current = null;
      }
    };
  }, [
    showMap,
    destLat,
    destLng,
    bakeryLat,
    bakeryLng,
    hasDriverPosition,
    isDriver,
    mapEl,
    refreshDrivingRoute,
  ]);

  // Live position updates — marker glide, trim route, smooth pan (no Directions API).
  useEffect(() => {
    const map = mapObj.current;
    if (!map || !showMap || destLat == null || destLng == null) return;

    const dest = { lat: destLat, lng: destLng };

    if (hasDriverPosition && driverLat != null && driverLng != null) {
      const target = { lat: driverLat, lng: driverLng };

      if (!driverMarkerRef.current) {
        driverMarkerRef.current = new window.google.maps.Marker({
          map,
          position: target,
          title: driverName,
          icon: {
            url: `data:image/svg+xml,${CAKE_MARKER_SVG}`,
            scaledSize: new window.google.maps.Size(44, 44),
            anchor: new window.google.maps.Point(22, 22),
          },
          zIndex: 4,
        });
        if (!didInitialFitRef.current) {
          fitCamera(map, pathRef.current, target, dest);
        }
      } else {
        animateMarkerTo(driverMarkerRef.current, target, livePosition ? 3200 : 2000);
      }

      if (pathRef.current.length > 1) {
        const trimmed = trimRouteFromPosition(pathRef.current, target);
        if (routeLineRef.current) {
          routeLineRef.current.setPath(trimmed);
        }
        const etaFromPath = estimateMinutesFromMeters(pathDistanceMeters(trimmed));
        if (etaFromPath != null && etaReadyRef.current) {
          setRouteEtaMinutes(etaFromPath);
        }

        // DA: keep re-framing road + home as the driver moves (throttled).
        if (isDriver) {
          const now = Date.now();
          if (now - lastDriverZoomRefitRef.current >= 2000) {
            lastDriverZoomRefitRef.current = now;
            applyDriverMapCamera(map, trimmed, target, dest, { fullscreen });
          }
        }
      }

      if (didInitialFitRef.current && !isDriver) {
        smoothPanTo(map, target, livePosition ? 1100 : 1400);
      }
    } else if (
      bakeryLat != null &&
      bakeryLng != null &&
      showMap &&
      tracking?.active !== false
    ) {
      const bakeryPos = { lat: bakeryLat, lng: bakeryLng };
      if (!driverMarkerRef.current) {
        driverMarkerRef.current = new window.google.maps.Marker({
          map,
          position: bakeryPos,
          title: "Leaving kitchen",
          opacity: 0.72,
          icon: {
            url: `data:image/svg+xml,${CAKE_MARKER_SVG}`,
            scaledSize: new window.google.maps.Size(44, 44),
            anchor: new window.google.maps.Point(22, 22),
          },
          zIndex: 3,
        });
      }
    } else if (driverMarkerRef.current) {
      driverMarkerRef.current.setMap(null);
      driverMarkerRef.current = null;
      lastRouteOriginRef.current = null;
    }
  }, [
    showMap,
    destLat,
    destLng,
    driverLat,
    driverLng,
    hasDriverPosition,
    bakeryLat,
    bakeryLng,
    driverName,
    livePosition,
    tracking?.active,
    fitCamera,
    isDriver,
    fullscreen,
  ]);

  useEffect(() => {
    if (!mapObj.current || !pathRef.current.length) return undefined;
    const id = window.setTimeout(() => {
      const map = mapObj.current;
      const pos = driverPosRef.current;
      const dest =
        destLat != null && destLng != null ? { lat: destLat, lng: destLng } : null;
      window.google?.maps?.event?.trigger(map, "resize");
      if (pos && dest) {
        fitCamera(map, pathRef.current, pos, dest);
      } else if (!didInitialFitRef.current) {
        fitMapToRoute(map, pathRef.current, mapFitPadding(fullscreen, variant));
        didInitialFitRef.current = true;
      }
    }, 200);
    return () => window.clearTimeout(id);
  }, [fullscreen, destLat, destLng, fitCamera, variant]);

  return {
    routeEtaMinutes: etaReady ? routeEtaMinutes : null,
    etaReady,
    hasDriver: hasFreshDriver,
    hasDriverPosition,
    driverName,
  };
}
